import {
  COMPOSER_MARKDOWN_TRANSFORMERS,
  COMPOSER_MARKDOWN_SHORTCUTS,
} from "./ComposerMarkdown";
import { ComposerEditorBridge } from "./ComposerEditorBridge";
import { forwardRef, useState, type ClipboardEvent } from "react";

import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { $convertFromMarkdownString } from "@lexical/markdown";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import { CodeNode, CodeHighlightNode } from "@lexical/code";

import {
  ComposerMentionNode,
  mentionTransformer,
  type ComposerMention,
} from "./ComposerMentionNode";

export type RichTextFormat =
  | "bold"
  | "italic"
  | "strike"
  | "code"
  | "link"
  | "heading"
  | "quote"
  | "list"
  | "ordered-list"
  | "task-list"
  | "code-block";
export type RichTextInputHandle = {
  getMarkdown: () => string;
  getElement: () => HTMLElement | null;
  getCaretRect: () => DOMRect | null;
  setMarkdown: (value: string) => void;
  insertMention: (mention: ComposerMention) => void;
  focus: () => void;
  format: (kind: RichTextFormat) => void;
};

export type RichTextInputProps = {
  initialValue: string;
  placeholder: string;
  mentions?: ComposerMention[];
  onChange: (value: string) => void;
  onSelectionChange?: (textBeforeCaret: string) => void;
  onKeyDown?: (event: globalThis.KeyboardEvent) => boolean;
  onPaste?: (event: ClipboardEvent<HTMLDivElement>) => void;
  onHeightChange?: (height: number) => void;
  listboxId?: string;
  activeOptionId?: string;
};

/** Lexical owns the document and selection. React never rewrites the editable DOM on typing. */
export const RichTextInput = forwardRef<
  RichTextInputHandle,
  RichTextInputProps
>(function RichTextInput(props, ref) {
  const [initialConfig] = useState(() => ({
    namespace: "composer",
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
      CodeNode,
      CodeHighlightNode,
      ComposerMentionNode,
    ],
    onError: (error: Error) => {
      throw error;
    },
    theme: {
      paragraph: "composer-paragraph",
      text: {
        bold: "font-bold",
        italic: "italic",
        strikethrough: "line-through",
        code: "composer-code",
      },
      heading: {
        h1: "text-xl font-bold",
        h2: "text-lg font-bold",
        h3: "font-bold",
      },
      quote: "border-l-2 border-stone-300 pl-3",
      code: "composer-code-block",
      list: {
        ul: "list-disc pl-6",
        ol: "list-decimal pl-6",
        listitem: "ml-1",
      },
      link: "text-lumen-600 underline",
    },
    editorState: () => {
      $convertFromMarkdownString(
        props.initialValue,
        [
          mentionTransformer(props.mentions ?? []),
          ...COMPOSER_MARKDOWN_TRANSFORMERS,
        ],
        undefined,
        true,
      );
    },
    // Initialization only; subsequent explicit draft replacements use the editor transaction below.
  }));
  return (
    <LexicalComposer initialConfig={initialConfig}>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            aria-label={props.placeholder}
            aria-multiline="true"
            aria-autocomplete="list"
            aria-controls={props.listboxId}
            aria-haspopup={props.listboxId ? "listbox" : undefined}
            aria-activedescendant={props.activeOptionId}
            className="composer-input size-full max-w-full overflow-y-auto px-4 py-3 font-sans text-base leading-6 break-words whitespace-pre-wrap outline-none sm:text-sm"
            spellCheck
            autoCapitalize="sentences"
          />
        }
        placeholder={
          <div className="pointer-events-none absolute inset-x-4 top-3 text-base leading-6 text-stone-400 sm:text-sm">
            {props.placeholder}
          </div>
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <ComposerEditorBridge {...props} ref={ref} />
      <HistoryPlugin />
      <ListPlugin />
      <CheckListPlugin />
      <LinkPlugin />
      <MarkdownShortcutPlugin transformers={COMPOSER_MARKDOWN_SHORTCUTS} />
    </LexicalComposer>
  );
});
